import { IS_DEV_ENV } from "constants/app.constants";
import { CalculationMethod } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import { IGroupBy } from "models";

import { PARAM_FIELD } from "../../../../constants";
import {
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY
} from "./feature";

// Same feature set as StackedBar (see stackedBar.ts) — same buttons, same
// defaults, full parity — except chartTypeParam's x/y roles are swapped: x is
// the numeric field (defaults to Oil Cum To Date), y is the category/bucket
// field. proxies.ts swaps these back before building the chart-svc request,
// since the wire contract (xField = bucket, yField = numeric) doesn't change.
const STACKED_BAR_HORIZONTAL_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, { show: true }),
  forecast: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  source: createEntityState(EntityType.SourceFeature, { show: true, enabled: true }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  timeStep: createEntityState(EntityType.TimeStepFeature, {
    show: true,
    enabled: true
  }),
  isProducingRateType: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  cutoff: createEntityState(EntityType.CutOffChartFeature, {
    show: true,
    enabled: true
  }),
  shutInMonths: createEntityState(EntityType.ShutInMonthsChartFeature, {
    show: true,
    enabled: true
  }),
  movingAverageDays: createEntityState(EntityType.MovingAverageDaysChartFeature, {
    show: true,
    enabled: true
  }),
  survivorBias: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  useSeriesStyleOverride: createEntityState(EntityType.ChartsFeature, {
    show: false,
    enabled: false,
    active: false
  }),
  useWeightedRatioAverage: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  lockUnits: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lasso: createEntityState(EntityType.LassoFeature, {
    show: true,
    enabled: true
  }),
  typewells: createEntityState(EntityType.ChartsFeature, {
    show: true
  }),
  p10: createEntityState(EntityType.P10Feature, {
    show: true
  }),
  p50: createEntityState(EntityType.P50Feature, {
    show: true
  }),
  p90: createEntityState(EntityType.P90Feature, {
    show: true
  }),
  average: createEntityState(EntityType.AverageFeature, {
    show: true
  }),
  normalization: createEntityState(EntityType.NormalizationFeature, {
    show: true,
    enabled: true
  }),
  chartFocus: createEntityState(EntityType.ChartFocusFeature, {
    show: true,
    enabled: true
  }),
  annotations: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {
    show: true,
    enabled: true
  }),
  calculationMethod: createEntityState(EntityType.CalculationMethodFeature, {
    properties: {
      method: CalculationMethod.Count
    }
  }),
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  // These x/y field objects double as the allow-list seed for
  // createMergeWithDefaults (see entities/utils.ts) — any key present here is
  // preserved when a real field selection is merged in on remount; any key
  // absent gets silently stripped. Keep in sync with the full field shape
  // (DEFAULT_CHART_TYPE_PARAM_FEATURE.properties.x/y in ./feature).
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {
    properties: {
      x: {
        group: "",
        subgroup: "",
        product: PARAM_FIELD.OilCumToDate.product,
        pdenSource: undefined,
        forecastFolder: undefined,
        forecastFolderExplicit: undefined,
        forecastPropertySuffix: undefined,
        ...PARAM_FIELD.OilCumToDate
      } as Partial<IGroupBy>,
      y: {
        group: "",
        subgroup: "",
        product: "",
        property: "Dates.Vintage_Year",
        title: "Vintage (Year)",
        defaultUnit: "",
        dataType: "Integer",
        canBin: true,
        bin: null,
        pdenSource: undefined,
        forecastFolder: undefined,
        forecastFolderExplicit: undefined,
        forecastPropertySuffix: undefined
      }
    }
  }),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {}),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {}),
  xAxisFieldSync: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: false
  }),
  xAxisSort: createEntityState(EntityType.XAxisSortFeature, {
    properties: {
      value: ""
    }
  }),
  splitYear: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: false
  }),
  hideOthers: createEntityState(EntityType.ChartsFeature, {
    show: true,
    active: false
  })
};

export const stackedBarHorizontalFeatures = createMergeWithDefaults(
  STACKED_BAR_HORIZONTAL_FEATURES
);
